import qs from 'qs';

import { definitions as BedbankApi } from '@luxuryescapes/contract-svc-bedbank';
import { operations } from '@luxuryescapes/contract-svc-bedbank/server';

import { BEDBANK_QUEUE_ACTION_MAP, getActionNameForQueue } from '~/utils/reservationRemediation';

import { json_headers, request } from './common';
import { HttpResponse } from './types';

const BEDBANK_FAILED_QUEUE_NAMES = Object.keys(BEDBANK_QUEUE_ACTION_MAP);

export interface MLMatchingCandidate {
  masterRoomId: string;
  masterRoomName: string;
  score: number;
  match: boolean;
}

export interface SourceRoomMLResult {
  candidates: Array<MLMatchingCandidate>;
  /** Composite keys identifying source rooms with this name. Format: "supplier:externalPropertyId:sourceRoomId" */
  sourceRooms: Array<string>;
  confidenceThreshold: number;
}

export type MLMatchingCandidatesResult = Record<string, SourceRoomMLResult>;

export default class BedbankService {
  static basePath() {
    return window.configs.API_HOST + '/api/bedbank';
  }

  static isBedbankId(str) {
    return /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i.test(str);
  }

  static getReservationInfo(reservationId) {
    return request(this.basePath() + `/reservations/${reservationId}`, {
      method: 'GET',
    });
  }

  static getRateInfo(options: {
    checkIn: string;
    checkOut: string;
    propertyId: string;
    region: string;
    brand: string;
    adults: string;
    children?: string | null;
    source?: string | null;
    allRates?: boolean;
    enablePropertyCollect?: boolean;
  }): Promise<HttpResponse<BedbankApi['AdminRatesForProperty']>> {
    let occupancy = `${options.adults}`;
    if (options.children) {
      occupancy = `${occupancy}-${options.children}`;
    }
    const params = new URLSearchParams({
      checkIn: `${options.checkIn}`,
      checkOut: `${options.checkOut}`,
      region: `${options.region}`,
      brand: `${options.brand}`,
      occupancy: `${occupancy}`,
      source: `${options.source || undefined}`,
    });
    if (options.allRates) {
      params.set('allRates', 'true');
    }
    if (options.enablePropertyCollect) {
      params.set('enablePropertyCollect', 'true');
    }
    return request(this.basePath() + `/properties/${options.propertyId}/get-rates?${params.toString()}`, {
      method: 'GET',
    });
  }

  static getBookingInfo(reservationId) {
    return request(this.basePath() + `/reservations/${reservationId}/supplier`, {
      method: 'GET',
    });
  }

  static getRebookableInfo(reservationIds) {
    return request(
      this.basePath() + `/reservations/rebookable?${reservationIds.map((id) => `reservationIds[]=${id}`).join('&')}`,
      {
        method: 'GET',
      },
    );
  }

  static checkRebookable(reservationId, ignoreCost = false) {
    return request(this.basePath() + `/reservations/${reservationId}/check_rebook?ignoreCost=${ignoreCost}`, {
      method: 'POST',
    });
  }

  static getPropertyById(id) {
    // NB: We set `preview` to include non-active properties.
    const baseUrl = `/properties/${id}?preview=true`;
    return request(this.basePath() + baseUrl, {
      method: 'GET',
    });
  }

  static getRatesScan(id) {
    const baseUrl = `/properties/${id}/rates/scan`;
    return request(this.basePath() + baseUrl, {
      method: 'GET',
    });
  }

  static postRatesScan(id) {
    const baseUrl = `/properties/${id}/rates/scan`;
    return request(this.basePath() + baseUrl, {
      method: 'POST',
    });
  }

  static getRatesSync(id) {
    const baseUrl = `/properties/${id}/rates/sync/job`;
    return request(this.basePath() + baseUrl, {
      method: 'GET',
    });
  }

  static postRatesSync(id) {
    const baseUrl = `/properties/${id}/rates/sync/job`;
    return request(this.basePath() + baseUrl, {
      method: 'POST',
    });
  }

  static updateSellPrices(id) {
    const baseUrl = `/properties/${id}/update-sell-prices`;
    return request(this.basePath() + baseUrl, {
      method: 'POST',
    });
  }

  static getAllCalendar(id) {
    const baseUrl = `/properties/${id}/calendar?region=AU&brand=luxuryescapes&all=true`;
    return request(this.basePath() + baseUrl, {
      method: 'GET',
    });
  }

  static getChains() {
    const baseUrl = '/chains';
    return request(this.basePath() + baseUrl, {
      method: 'GET',
    });
  }

  static getMappedRooms(id) {
    const baseUrl = `/properties/${id}/mapped-rooms`;
    return request(this.basePath() + baseUrl, {
      method: 'GET',
    });
  }

  static getSourceRooms(id) {
    const baseUrl = `/properties/${id}/source-rooms`;
    return request(this.basePath() + baseUrl, {
      method: 'GET',
    });
  }

  static getMLMatchingCandidates(propertyId: string): Promise<HttpResponse<MLMatchingCandidatesResult>> {
    const baseUrl = `/properties/${propertyId}/ml-matching-candidates`;
    return request(this.basePath() + baseUrl, {
      method: 'GET',
    });
  }

  static createMappedRooms(id, payload) {
    const baseUrl = `/properties/${id}/map-rooms`;
    return request(this.basePath() + baseUrl, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static deleteMappedRooms(id, payload) {
    const baseUrl = `/properties/${id}/map-rooms`;
    return request(this.basePath() + baseUrl, {
      method: 'DELETE',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static getPropertiesUrlQuery(params) {
    const uri = [];
    const {
      query,
      type,
      propertyType,
      country,
      status,
      ratingOp,
      ratingVal,
      city,
      stateProvince,
      domain,
      comment,
      profile,
      chain,
      promotions,
      chunk,
      startOffset,
      googleRatingCount,
      googleRatingOp,
      googleRatingVal,
      paymentMethods,
      supplierRatingSupplier,
      supplierStarRatingOp,
      supplierStarRatingVal,
      supplierGuestRatingOp,
      supplierGuestRatingVal,
      supplierReviewCountMin,
      supplierReviewCountMax,
      holidayTypes,
    } = params;

    if (query && type) {
      uri.push(`q=${encodeURIComponent(query)}`);
      uri.push(`type=${type}`);
    }
    if (propertyType) {
      uri.push(`propertyType=${propertyType}`);
    }
    if (status) {
      uri.push(`status=${status}`);
    }
    if (country) {
      uri.push(`country=${encodeURIComponent(country)}`);
    }
    if (chain) {
      uri.push(`chain=${encodeURIComponent(chain)}`);
    }
    if (promotions) {
      uri.push(`promotions=${encodeURIComponent(promotions)}`);
    }
    if (ratingOp && ratingVal) {
      uri.push(`ratingOp=${ratingOp}`);
      uri.push(`ratingVal=${ratingVal}`);
    }
    if (city) {
      uri.push(`city=${encodeURIComponent(city)}`);
    }
    if (stateProvince) {
      uri.push(`stateProvince=${encodeURIComponent(stateProvince)}`);
    }
    if (domain) {
      uri.push(`domain=${domain}`);
    }
    if (comment) {
      uri.push(`comment=${comment}`);
    }
    if (profile) {
      uri.push(`profile=${profile}`);
    }
    if (chunk) {
      uri.push(`chunk=${chunk}`);
    }
    if (startOffset >= 0) {
      uri.push(`startOffset=${startOffset}`);
    }
    if (googleRatingCount) {
      uri.push(`googleRatingCount=${googleRatingCount}`);
    }
    if (googleRatingOp && googleRatingVal) {
      uri.push(`googleRatingOp=${googleRatingOp}`);
      uri.push(`googleRatingVal=${googleRatingVal}`);
    }
    if (paymentMethods) {
      uri.push(`paymentMethods=${encodeURIComponent(paymentMethods)}`);
    }
    if (holidayTypes) {
      uri.push(`holidayTypes=${encodeURIComponent(holidayTypes)}`);
    }

    if (supplierRatingSupplier) {
      uri.push(`supplierRatingSupplier=${encodeURIComponent(supplierRatingSupplier)}`);

      if (supplierStarRatingOp && supplierStarRatingVal) {
        uri.push(`supplierStarRatingOp=${supplierStarRatingOp}`);
        uri.push(`supplierStarRatingVal=${supplierStarRatingVal}`);
      }

      if (supplierGuestRatingOp && supplierGuestRatingVal) {
        uri.push(`supplierGuestRatingOp=${supplierGuestRatingOp}`);
        uri.push(`supplierGuestRatingVal=${supplierGuestRatingVal}`);
      }

      if (supplierReviewCountMin) {
        uri.push(`supplierReviewCountMin=${supplierReviewCountMin}`);
      }

      if (supplierReviewCountMax) {
        uri.push(`supplierReviewCountMax=${supplierReviewCountMax}`);
      }
    }

    return uri.join('&');
  }

  static manageSuppliersProperties({
    id,
    action,
    masterPropertyId,
  }: operations['/api/bedbank/suppliers/properties/{id}/action/{action}/post']['requestBody']['content']['application/json'] &
    operations['/api/bedbank/suppliers/properties/{id}/action/{action}/post']['parameters']['path']): Promise<
    operations['/api/bedbank/suppliers/properties/{id}/action/{action}/post']['responses']['200']['content']['application/json']
  > {
    const fullUrl = new URL(this.basePath() + `/suppliers/properties/${id}/action/${action}`);

    return request(fullUrl.toString(), {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({
        masterPropertyId,
      }),
    });
  }

  static jobsSuppliersProperties({
    id,
  }: operations['/api/bedbank/suppliers/properties/{id}/jobs/get']['parameters']['path']): Promise<
    operations['/api/bedbank/suppliers/properties/{id}/jobs/get']['responses']['200']['content']['application/json']
  > {
    const fullUrl = new URL(this.basePath() + `/suppliers/properties/${id}/jobs`);

    return request(fullUrl.toString(), {
      method: 'GET',
      headers: json_headers,
    });
  }

  static checkSuppliersProperties({
    id,
    propertyId,
  }: operations['/api/bedbank/suppliers/properties/{id}/check/{propertyId}/get']['parameters']['path']): Promise<
    operations['/api/bedbank/suppliers/properties/{id}/check/{propertyId}/get']['responses']['200']['content']['application/json']
  > {
    const fullUrl = new URL(this.basePath() + `/suppliers/properties/${id}/check/${propertyId}`);

    return request(fullUrl.toString(), {
      method: 'GET',
      headers: json_headers,
    });
  }

  static getSuppliersProperties({
    page,
    limit,
    sortBy,
    orderBy,
    filterById,
    filterBySupplierPropertyId,
    filterByMasterPropertyId,
    filterByName,
    filterBySupplier,
    filterByCountryCode,
    filterByHasImages,
    filterByHasRooms,
    filterByMapped,
    filterByRatingOp,
    filterByRatingVal,
  }: operations['/api/bedbank/suppliers/properties/get']['parameters']['query']): Promise<
    operations['/api/bedbank/suppliers/properties/get']['responses']['200']['content']['application/json']
  > {
    const fullUrl = new URL(this.basePath() + '/suppliers/properties');

    fullUrl.searchParams.append('page', page.toString());
    fullUrl.searchParams.append('limit', limit.toString());
    fullUrl.searchParams.append('sortBy', sortBy);
    fullUrl.searchParams.append('orderBy', orderBy);

    if (filterById) {
      fullUrl.searchParams.append('filterById', filterById);
    }
    if (filterBySupplierPropertyId) {
      fullUrl.searchParams.append('filterBySupplierPropertyId', filterBySupplierPropertyId);
    }
    if (filterByMasterPropertyId) {
      fullUrl.searchParams.append('filterByMasterPropertyId', filterByMasterPropertyId);
    }
    if (filterByName) {
      fullUrl.searchParams.append('filterByName', filterByName);
    }
    if (filterBySupplier) {
      fullUrl.searchParams.append('filterBySupplier', filterBySupplier);
    }
    if (filterByCountryCode) {
      filterByCountryCode.forEach((code, index) => {
        fullUrl.searchParams.append(`filterByCountryCode[${index}]`, code);
      });
    }
    if (filterByHasImages) {
      fullUrl.searchParams.append('filterByHasImages', filterByHasImages);
    }
    if (filterByHasRooms) {
      fullUrl.searchParams.append('filterByHasRooms', filterByHasRooms);
    }
    if (filterByMapped) {
      fullUrl.searchParams.append('filterByMapped', filterByMapped);
    }
    if (filterByRatingOp) {
      fullUrl.searchParams.append('filterByRatingOp', filterByRatingOp);
    }
    if (filterByRatingVal) {
      fullUrl.searchParams.append('filterByRatingVal', filterByRatingVal.toString());
    }
    return request(fullUrl.toString(), {
      method: 'GET',
    });
  }

  static pullSupplierPropertyContent({
    externalId,
    supplier,
  }: operations['/api/bedbank/suppliers/properties/post']['requestBody']['content']['application/json']): Promise<
    operations['/api/bedbank/suppliers/properties/post']['responses']['200']['content']['application/json']
  > {
    const fullUrl = new URL(this.basePath() + '/suppliers/properties');

    return request(fullUrl.toString(), {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({
        externalId,
        supplier,
      }),
    });
  }

  static getProperties(page, limit, query) {
    let uri = `/properties/list?page=${page}&limit=${limit}`;
    const additional = BedbankService.getPropertiesUrlQuery(query);

    if (additional) {
      uri = `${uri}&${additional}`;
    }

    return request(this.basePath() + uri, {
      method: 'GET',
    });
  }

  static getPropertiesByName(page, limit, q) {
    return this.getProperties(page, limit, { query: q, type: 'name' });
  }

  static createPropertyImage(id, payload) {
    return request(this.basePath() + `/properties/add-image/${id}`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static createRoomImage(id, payload) {
    return request(this.basePath() + `/properties/room/add-image/${id}`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static getPropertyEdit(id) {
    return request(this.basePath() + `/properties/${id}/edit`, {
      method: 'GET',
    });
  }

  static patchPropertyEdit(id, payload) {
    return request(this.basePath() + `/properties/${id}/edit`, {
      method: 'PATCH',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static runRoomMapping(id) {
    return request(this.basePath() + `/properties/${id}/run-map-rooms`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({}),
    }).catch((error) => {
      if (error.status === 422 && error.message.toLowerCase().includes('already in progress')) {
        return {
          status: 200,
          message: 'Room mapping is already in progress',
          result: {
            id,
            type: 'roomMapping',
            state: 'accepted',
          },
        };
      } else {
        throw error;
      }
    });
  }

  static runAvailabilityScore(id) {
    return request(this.basePath() + `/properties/${id}/run-availability-score`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({}),
    });
  }

  static runDecoupleRooms(id, payload) {
    return request(this.basePath() + `/properties/${id}/decouple-rooms`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static getMappingIssues({
    issueType,
    issueStatus,
    page,
    limit,
    bookingNumber,
    withFutureBookings,
    supplierName,
  }: {
    issueType: App.Bedbank.IssueType;
    issueStatus: Array<string>;
    page: number;
    limit: number;
    bookingNumber?: string;
    withFutureBookings?: boolean;
    supplierName?: string;
  }) {
    const queryParams: any = { issueType, issueStatus, page, limit };
    if (bookingNumber) {
      queryParams.bookingNumber = bookingNumber;
    }
    if (withFutureBookings !== undefined) {
      queryParams.withFutureBookings = withFutureBookings;
    }
    if (supplierName) {
      queryParams.supplierName = supplierName;
    }
    return request(this.basePath() + `/issues/mapping-issues?${qs.stringify(queryParams)}`, {
      method: 'GET',
    });
  }

  static resolveMappingIssues(
    payload: operations['/api/bedbank/issues/mapping-issues-resolution/post']['requestBody']['content']['application/json'],
  ) {
    return request(this.basePath() + `/issues/mapping-issues-resolution`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static batchResolveMappingIssues(
    payload: operations['/api/bedbank/issues/mapping-issues-resolution/batch/post']['requestBody']['content']['application/json'],
  ) {
    return request(this.basePath() + `/issues/mapping-issues-resolution/batch`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static runRoomGeneration(id) {
    return request(this.basePath() + `/properties/${id}/map-rooms/generate`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({}),
    });
  }

  static getProgressStatus(id, type) {
    return request(this.basePath() + `/progress/status?id=${id}&type=${type}`, {
      method: 'GET',
    });
  }

  static bulkPatchPropertiesEdit(payload) {
    return request(this.basePath() + `/properties/edit`, {
      method: 'PATCH',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static patchShowHideImages(id, payload) {
    return request(this.basePath() + `/properties/${id}/batch-property-image-show-hide`, {
      method: 'PATCH',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static remapDecoupledRoom(propertyId, roomId, payload) {
    return request(this.basePath() + `/properties/${propertyId}/rooms/${roomId}/remap`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static patchRoom(propertyId, roomId, payload) {
    return request(this.basePath() + `/properties/${propertyId}/rooms/${roomId}`, {
      method: 'PATCH',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static setAutomappingFlag(id, payload) {
    return request(this.basePath() + `/properties/${id}/auto-map-rooms`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static syncPropertyContent(id) {
    return request(this.basePath() + `/properties/${id}/content/sync`, {
      method: 'POST',
    });
  }

  static getReservationSourceRoom(id) {
    return request(this.basePath() + `/reservations/${id}/source-rooms`, {
      method: 'GET',
    });
  }

  static getReservationsRoomsInfo(reservationIds) {
    return request(
      this.basePath() + `/reservations/rooms/info?${reservationIds.map((id) => `reservationIds[]=${id}`).join('&')}`,
      {
        method: 'GET',
      },
    );
  }

  static getDynamicConfigurations() {
    return request(this.basePath() + '/dynamic-configs', {
      method: 'GET',
    });
  }

  static getRoomComments(externalId, externalPropertyId, supplier) {
    const baseUrl = `/rooms/comments?externalId=${externalId}&externalPropertyId=${externalPropertyId}&supplier=${supplier}`;
    return request(this.basePath() + baseUrl, {
      method: 'GET',
    });
  }

  static addRoomComment(payload) {
    const baseUrl = `/rooms/comments`;
    return request(this.basePath() + baseUrl, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static deleteRoomComment(commentId) {
    const baseUrl = `/rooms/comments/${commentId}`;
    return request(this.basePath() + baseUrl, {
      method: 'DELETE',
      headers: json_headers,
    });
  }

  // return a subset fields of bedbank reservation, employee permission instead of admin permission
  static getReservationInfoEmployeeView(reservationId) {
    const baseUrl = `/reservations/${reservationId}/employee-view`;
    return request(this.basePath() + baseUrl, {
      method: 'GET',
    });
  }

  static toggleReservationMarginMax(reservationId: string, disableMarginMax: boolean) {
    const baseUrl = `/reservations/${reservationId}/rebook`;
    return request(this.basePath() + baseUrl, {
      method: 'PATCH',
      headers: json_headers,
      body: JSON.stringify({ disableMarginMax }),
    });
  }

  static getRoomMappingCandidates(params?: {
    isApproved?: boolean;
    isArchived?: boolean;
  }): Promise<App.Bedbank.RoomMappingCandidatesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.isApproved !== undefined) {
      queryParams.set('isApproved', String(params.isApproved));
    }
    if (params?.isArchived !== undefined) {
      queryParams.set('isArchived', String(params.isArchived));
    }
    const queryString = queryParams.toString();
    const url = `/properties/room-mapping-candidates${queryString ? `?${queryString}` : ''}`;
    return request(this.basePath() + url, {
      method: 'GET',
    });
  }

  static approveRoomMappingCandidates(
    candidateIds: Array<string>,
  ): Promise<App.Bedbank.ApproveRoomMappingCandidatesResponse> {
    return request(this.basePath() + '/properties/room-mapping-candidates/approve', {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({ candidateIds }),
    });
  }

  static archiveRoomMappingCandidates(
    candidateIds: Array<string>,
  ): Promise<App.Bedbank.ArchiveRoomMappingCandidatesResponse> {
    return request(this.basePath() + '/properties/room-mapping-candidates/archive', {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({ candidateIds }),
    });
  }

  static unarchiveRoomMappingCandidates(
    candidateIds: Array<string>,
  ): Promise<App.Bedbank.UnarchiveRoomMappingCandidatesResponse> {
    return request(this.basePath() + '/properties/room-mapping-candidates/unarchive', {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({ candidateIds }),
    });
  }

  static getPotentialPropertyMappings(
    filters?: App.Bedbank.PotentialPropertyMappingsFilters,
    options?: { signal?: AbortSignal },
  ): Promise<App.Bedbank.PotentialPropertyMappingsResponse> {
    const query = qs.stringify(
      {
        supplier: filters?.supplier,
        masterPropertyDomain: filters?.masterPropertyDomain,
        masterPropertyStatus: filters?.masterPropertyStatus,
        masterPropertyRegion: filters?.masterPropertyRegion,
        masterPropertyStarRating: filters?.masterPropertyStarRating,
        confidence: filters?.confidence,
      },
      { skipNulls: true },
    );
    const url = this.basePath() + '/properties/potential-mappings' + (query ? `?${query}` : '');
    return request(url, { method: 'GET', signal: options?.signal });
  }

  static approvePotentialPropertyMapping(mappingId: string): Promise<App.Bedbank.PotentialPropertyMappingResponse> {
    return request(this.basePath() + `/properties/potential-mappings/${mappingId}/approve`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({}),
    });
  }

  static rejectPotentialPropertyMapping(mappingId: string): Promise<App.Bedbank.PotentialPropertyMappingResponse> {
    return request(this.basePath() + `/properties/potential-mappings/${mappingId}/reject`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({}),
    });
  }

  static async getFailedJobs() {
    const resp: any = await request(`${this.basePath()}/jobs/api/queues?page=1&jobsPerPage=10`, {
      method: 'GET',
    });

    const failedQueues = Object.values(resp.queues)
      .filter((queue: any) => queue.counts.failed > 0 && BEDBANK_FAILED_QUEUE_NAMES.includes(queue.name))
      .map((queue: any) => queue.name);

    const failedReservations = await Promise.all(
      failedQueues.map(async (queue) => {
        const queueResp: any = await request(
          `${this.basePath()}/jobs/api/queues?activeQueue=${queue}&status=failed&page=1&jobsPerPage=50`,
          {
            method: 'GET',
          },
        );
        return queueResp.queues.find((q: any) => q.name === queue);
      }),
    );

    const result = failedReservations.flatMap((queue: any) => {
      if (!queue) {
        return [];
      }

      return queue.jobs.map((job: any) => ({
        ...job,
        data: {
          ...job.data,
          actionName: job.data?.actionName ?? getActionNameForQueue(queue.name),
          queueName: queue.name,
          jobId: job.id,
          source: 'bedbank',
        },
      }));
    });

    return result;
  }

  static clearFailedJob(queueName: string, jobId: string | number, resolution: string) {
    return request(
      `${this.basePath()}/jobs/api/queues/${queueName}/${jobId}/clean?resolution=${encodeURIComponent(resolution)}`,
      {
        method: 'PUT',
      },
    );
  }

  static getReservationLogs(reservationId: string) {
    return request(`${this.basePath()}/reservations/${reservationId}/logs`, {
      method: 'GET',
      headers: json_headers,
    });
  }
}
